import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileText,
  Headphones,
  MessageSquareText,
  PencilLine,
  PhoneCall,
  Save,
  Ticket,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import { useAuth } from '../auth';
import { userHasAnyRole } from '../roles';
import { CallRouteDetails } from '../telephony/components/CallRouteDetails';
import type { BrowserCall } from '../telephony/types';
import { clientHistoryApi } from './api';
import {
  clientCallPresentation,
  companyNotePresentation,
  formatClientHistoryDate,
  mayEditCompanyNote,
} from './presentation';
import type {
  ClientHistoryContactOption,
  ClientHistoryFilters,
  ClientHistoryScope,
  CompanyNote,
} from './types';

type Props = {
  scope: ClientHistoryScope;
  entityId: string;
  contacts?: ClientHistoryContactOption[];
  compact?: boolean;
};

const RECORDING_NOTES: Record<string, string> = {
  PENDING: 'Проигрыватель появится после обработки записи',
  DELETING: 'Файл удаляется по политике хранения',
  DELETED: 'Аудиофайл удалён, сведения о звонке сохранены',
  DENIED: 'Для этой записи недостаточно прав',
  FAILED: 'Аудиофайл нельзя воспроизвести',
  EMPTY: 'Запись разговора отсутствует',
};

function isoDateBoundary(value: string, endOfDay = false): string {
  if (!value) return '';
  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function ClientCallCard({ call, occurredAt, compact = false }: { call: BrowserCall; occurredAt: string; compact?: boolean }) {
  const [routeExpanded, setRouteExpanded] = useState(false);
  const presentation = clientCallPresentation(call);
  const inbound = call.direction === 'INBOUND';
  const recordingNote = RECORDING_NOTES[presentation.recording.kind] || 'Аудиофайл нельзя воспроизвести';

  return <article className="client-history-call-card">
    <header>
      <span className={`client-history-call-direction ${inbound ? 'inbound' : 'outbound'}`}>
        {inbound ? <ArrowDownLeft /> : <ArrowUpRight />}
      </span>
      <div>
        <b>{presentation.title}</b>
        <span>{presentation.party}</span>
      </div>
      <time>{formatClientHistoryDate(occurredAt)}</time>
    </header>

    <div className="client-history-call-summary">
      <strong>{presentation.number}</strong>
      <span className={`client-history-call-result ${presentation.statusTone}`}>{presentation.statusLabel}</span>
    </div>

    <div className="client-history-call-meta">
      <span><Clock3 /> {presentation.durationLabel}</span>
      <span><UserRound /> {presentation.operator}</span>
      <span><Headphones /> {presentation.extension}</span>
      <span>{presentation.directionLabel}</span>
    </div>

    {presentation.ticket && <Link className="client-history-related-ticket" to={`/tickets/${call.ticket_id}`}>
      <Ticket />
      <span><small>Связанная заявка</small><b>{presentation.ticket}</b></span>
    </Link>}

    {presentation.comment && <div className="client-history-call-comment">
      <MessageSquareText />
      <span><small>Итог звонка</small><b>{presentation.comment}</b></span>
    </div>}

    <div className={`client-history-recording ${presentation.recording.kind.toLowerCase()}`}>
      <Headphones />
      <div>
        <b>{presentation.recording.label}</b>
        {presentation.recording.kind === 'PLAYER'
          ? <audio aria-label={presentation.recording.label} controls preload="none" src={presentation.recording.url} />
          : <small>{recordingNote}</small>}
      </div>
    </div>

    {!compact && call.route_trace && <CallRouteDetails
      callId={call.id}
      trace={call.route_trace}
      expanded={routeExpanded}
      onToggle={() => setRouteExpanded(value => !value)}
    />}
  </article>;
}

function CompanyNoteCard({
  note,
  onEdit,
  onDelete,
  deleting,
}: {
  note: CompanyNote;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { user } = useAuth();
  const view = companyNotePresentation(note);
  const editable = mayEditCompanyNote(note, user);
  return <article className={`client-history-note-card ${note.is_important ? 'important' : ''}`}>
    <span className="client-history-note-icon">{note.is_important ? <AlertCircle /> : <FileText />}</span>
    <div>
      <header>
        <span><b>{view.importantLabel}</b><small>{view.author}</small></span>
        <time>{formatClientHistoryDate(note.created_at)}</time>
      </header>
      <p>{view.body}</p>
      {view.auditLabel && <small className="client-history-note-audit">{view.auditLabel}</small>}
    </div>
    {editable && <div className="client-history-note-actions">
      <button type="button" className="icon-button" title="Редактировать заметку" onClick={onEdit}><PencilLine /></button>
      <button type="button" className="icon-button danger" title="Удалить заметку" disabled={deleting} onClick={onDelete}><Trash2 /></button>
    </div>}
  </article>;
}

export function ClientInteractionHistory({ scope, entityId, contacts = [], compact = false }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'ALL' | 'CALL' | 'NOTE'>('ALL');
  const [direction, setDirection] = useState<'' | 'INBOUND' | 'OUTBOUND'>('');
  const [status, setStatus] = useState('');
  const [contactId, setContactId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [noteBody, setNoteBody] = useState('');
  const [noteImportant, setNoteImportant] = useState(false);
  const [editingNote, setEditingNote] = useState<CompanyNote | null>(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const pageSize = compact ? 5 : 10;
  const canWriteNotes = scope === 'company'
    && user?.userType === 'EMPLOYEE'
    && userHasAnyRole(user, ['ADMIN', 'MANAGER', 'DISPATCHER', 'EXECUTOR']);

  useEffect(() => {
    setPage(1);
    setKind('ALL');
    setDirection('');
    setStatus('');
    setContactId('');
    setDateFrom('');
    setDateTo('');
    setEditingNote(null);
    setError('');
  }, [entityId, scope]);

  const filters = useMemo<ClientHistoryFilters>(() => ({
    kind: scope === 'contact' ? 'CALL' : kind,
    direction,
    status,
    contactId: scope === 'company' ? contactId : '',
    dateFrom: isoDateBoundary(dateFrom),
    dateTo: isoDateBoundary(dateTo, true),
    page,
    pageSize,
  }), [scope, kind, direction, status, contactId, dateFrom, dateTo, page, pageSize]);

  const historyKey = ['client-history', scope, entityId, filters] as const;
  const { data, isLoading, isFetching, isError, error: queryError } = useQuery({
    queryKey: historyKey,
    queryFn: () => clientHistoryApi.history(scope, entityId, filters),
    enabled: Boolean(entityId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client-history', scope, entityId] });
  };

  const createNote = useMutation({
    mutationFn: () => clientHistoryApi.createNote(entityId, { body: noteBody, isImportant: noteImportant }),
    onSuccess: async () => {
      setNoteBody('');
      setNoteImportant(false);
      setFeedback('Заметка добавлена в историю компании');
      setError('');
      setPage(1);
      await refresh();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось добавить заметку'),
  });

  const updateNote = useMutation({
    mutationFn: (note: CompanyNote) => clientHistoryApi.updateNote(entityId, note.id, {
      body: note.body,
      isImportant: note.is_important,
    }),
    onSuccess: async () => {
      setEditingNote(null);
      setFeedback('Заметка обновлена');
      setError('');
      await refresh();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось обновить заметку'),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: string) => clientHistoryApi.deleteNote(entityId, noteId),
    onSuccess: async () => {
      setFeedback('Заметка удалена из истории');
      setError('');
      await refresh();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось удалить заметку'),
  });

  const resetPage = () => setPage(1);
  const submitNote = (event: React.FormEvent) => {
    event.preventDefault();
    if (!noteBody.trim() || createNote.isPending) return;
    createNote.mutate();
  };
  const reportedError = error || (isError ? (queryError instanceof Error ? queryError.message : 'Не удалось загрузить историю') : '');

  return <section className={`client-interaction-history ${compact ? 'compact' : ''}`}>
    <div className="client-history-heading">
      <div>
        <span className="client-history-heading-icon"><PhoneCall /></span>
        <span><h3>{scope === 'company' ? 'История общения' : 'История звонков'}</h3><p>{scope === 'company' ? 'Звонки всех контактов и внутренние заметки' : 'Звонки, связанные с этим контактом'}</p></span>
      </div>
      {isFetching && !isLoading && <small>Обновляем…</small>}
    </div>

    {canWriteNotes && !compact && <form className="client-history-note-composer" onSubmit={submitNote}>
      <textarea
        rows={3}
        maxLength={4000}
        value={noteBody}
        onChange={event => setNoteBody(event.target.value)}
        placeholder="Добавить внутреннюю заметку о компании…"
      />
      <footer>
        <label><input type="checkbox" checked={noteImportant} onChange={event => setNoteImportant(event.target.checked)} /> Важная заметка</label>
        <span>{noteBody.length}/4000</span>
        <button type="submit" className="button primary compact" disabled={!noteBody.trim() || createNote.isPending}><MessageSquareText /> {createNote.isPending ? 'Добавляем…' : 'Добавить заметку'}</button>
      </footer>
    </form>}

    {!compact && <div className="client-history-filters">
      {scope === 'company' && <label><span>События</span><select value={kind} onChange={event => { setKind(event.target.value as typeof kind); resetPage(); }}><option value="ALL">Вся история</option><option value="CALL">Только звонки</option><option value="NOTE">Только заметки</option></select></label>}
      <label><span>Направление</span><select value={direction} onChange={event => { setDirection(event.target.value as typeof direction); resetPage(); }}><option value="">Все звонки</option><option value="INBOUND">Входящие</option><option value="OUTBOUND">Исходящие</option></select></label>
      <label><span>Результат</span><select value={status} onChange={event => { setStatus(event.target.value); resetPage(); }}><option value="">Все результаты</option><option value="COMPLETED">Завершён</option><option value="ANSWERED">Отвечен</option><option value="MISSED">Пропущен</option><option value="FAILED">Ошибка</option><option value="RINGING">Без завершения</option></select></label>
      {scope === 'company' && contacts.length > 0 && <label><span>Контакт</span><select value={contactId} onChange={event => { setContactId(event.target.value); resetPage(); }}><option value="">Все контакты</option>{contacts.map(contact => <option key={contact.id} value={contact.id}>{contact.full_name || 'Без имени'}</option>)}</select></label>}
      <label><span>С даты</span><input type="date" value={dateFrom} onChange={event => { setDateFrom(event.target.value); resetPage(); }} /></label>
      <label><span>По дату</span><input type="date" value={dateTo} onChange={event => { setDateTo(event.target.value); resetPage(); }} /></label>
      <button type="button" className="button ghost compact" onClick={() => { setKind('ALL'); setDirection(''); setStatus(''); setContactId(''); setDateFrom(''); setDateTo(''); setPage(1); }}>Сбросить</button>
    </div>}

    {compact && <div className="client-history-compact-filters">
      <select aria-label="Направление звонков" value={direction} onChange={event => { setDirection(event.target.value as typeof direction); resetPage(); }}><option value="">Все звонки</option><option value="INBOUND">Входящие</option><option value="OUTBOUND">Исходящие</option></select>
      <select aria-label="Результат звонков" value={status} onChange={event => { setStatus(event.target.value); resetPage(); }}><option value="">Все результаты</option><option value="COMPLETED">Завершённые</option><option value="MISSED">Пропущенные</option><option value="FAILED">С ошибкой</option></select>
    </div>}

    {feedback && <div className="client-history-feedback success">{feedback}<button type="button" onClick={() => setFeedback('')}><X /></button></div>}
    {reportedError && <div className="client-history-feedback error">{reportedError}<button type="button" onClick={() => setError('')}><X /></button></div>}

    <div className="client-history-timeline" aria-busy={isLoading || isFetching}>
      {isLoading && <div className="client-history-loading"><span /><span /><span /></div>}
      {!isLoading && data?.rows.map(row => row.kind === 'CALL'
        ? <ClientCallCard key={row.id} call={row.call} occurredAt={row.occurred_at} compact={compact} />
        : editingNote?.id === row.note.id
          ? <form key={row.id} className="client-history-note-edit" onSubmit={event => { event.preventDefault(); updateNote.mutate(editingNote); }}>
            <textarea rows={4} maxLength={4000} value={editingNote.body} onChange={event => setEditingNote({ ...editingNote, body: event.target.value })} />
            <label><input type="checkbox" checked={editingNote.is_important} onChange={event => setEditingNote({ ...editingNote, is_important: event.target.checked })} /> Важная заметка</label>
            <div><button type="button" className="button ghost compact" onClick={() => setEditingNote(null)}>Отмена</button><button type="submit" className="button primary compact" disabled={!editingNote.body.trim() || updateNote.isPending}><Save /> Сохранить</button></div>
          </form>
          : <CompanyNoteCard
            key={row.id}
            note={row.note}
            deleting={deleteNote.isPending}
            onEdit={() => setEditingNote({ ...row.note })}
            onDelete={() => { if (window.confirm('Удалить эту заметку из истории компании?')) deleteNote.mutate(row.note.id); }}
          />)}
      {!isLoading && !data?.rows.length && <div className="client-history-empty"><PhoneCall /><b>История пока пуста</b><span>{scope === 'company' ? 'Здесь появятся звонки и заметки о компании.' : 'Здесь появятся звонки этого контакта.'}</span></div>}
    </div>

    {data && data.total > 0 && <footer className="client-history-pagination">
      <span>Показано {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} из {data.total}</span>
      <div><button type="button" disabled={!data.hasPrevious || isFetching} onClick={() => setPage(current => Math.max(1, current - 1))}><ChevronLeft /> Назад</button><em>{data.page}</em><button type="button" disabled={!data.hasNext || isFetching} onClick={() => setPage(current => current + 1)}>Далее <ChevronRight /></button></div>
    </footer>}
  </section>;
}
